/**
 * useVoorinspectieItem — laden, form-state, opslaan en navigatie van één
 * voorinspectie-item.
 *
 * Gedeeld door de legacy-render (VoorinspectieDetailPage) en de Werkbank-render
 * (VoorinspectieDetailPageWb). De payload van handleSave is hier de enige bron;
 * beide renders schrijven exact hetzelfde naar saveVoorinspectie en naar de
 * offline-wachtrij. De risico-afrond-guard (schade aangevinkt zonder melding,
 * WI-332384) leeft hier ook.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesforceClient } from '../lib/salesforce/client';
import { isServerWeigering } from '../lib/sync/wasserij-wachtrij';
import { RISICO_INDICATIE_VOORINSPECTIE } from '../lib/constants';
import {
  getCachedVoorinspectieItems,
  queuePendingVoorinspectie,
  removeFromVoorinspectieCache,
} from '../lib/db/database';
import type { VoorinspectieItem, VormType } from '../lib/salesforce';

export function useVoorinspectieItem() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();

  // Data state
  const [item, setItem] = useState<VoorinspectieItem | null>(null);
  const [allItems, setAllItems] = useState<VoorinspectieItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Weigering van de server bij opslaan: tonen, niet in de wachtrij (#144).
  const [saveFout, setSaveFout] = useState<string | null>(null);

  // Form state - Dimensions (nameten) - default vorm to Rechthoek
  const [vorm, setVorm] = useState<VormType>('Rechthoek');
  const [lengthCm, setLengthCm] = useState<string>('');
  const [widthCm, setWidthCm] = useState<string>('');
  const [diameterCm, setDiameterCm] = useState<string>('');
  const [aantal, setAantal] = useState<string>(''); // For vorm "Anders" - surface area in m²

  // Form state - Other fields
  const [selectedVoorinspectie, setSelectedVoorinspectie] = useState<string[]>([]);
  const [soort, setSoort] = useState('');
  const [merk, setMerk] = useState('');
  const [voorinspectieOpmerkingen, setVoorinspectieOpmerkingen] = useState('');
  const [wasrapport, setWasrapport] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Risico-flow state
  const [risicoDialogOpen, setRisicoDialogOpen] = useState(false);
  // Afrond-guard: gezet wanneer Opslaan wordt ingedrukt met een
  // schade-indicerende voorinspectie-optie terwijl er geen melding loopt.
  // Onthoudt de goToNext-keuze zodat "Afronden zonder melding" dezelfde
  // save alsnog uitvoert.
  const [risicoAfrondPrompt, setRisicoAfrondPrompt] = useState<{ goToNext: boolean } | null>(null);
  const [showCamera, setShowCamera] = useState(false); // media-laag (MEDIA_EIGEN)
  const [mediaRefresh, setMediaRefresh] = useState(0); // MediaStrip herladen na capture

  // Load item data
  const loadItem = useCallback(async (opts?: { silent?: boolean }) => {
    if (!itemId) return;

    if (!opts?.silent) {
      setIsLoading(true);
      setError(null);
    }

    try {
        // Try API first
        const response = await salesforceClient.getVoorinspectieItems();

        if (response.success && response.data?.items) {
          setAllItems(response.data.items);
          const foundItem = response.data.items.find((i) => i.itemId === itemId);

          if (foundItem) {
            setItem(foundItem);
            if (!opts?.silent) {
              // Pre-fill form with existing values if any (alleen op eerste load —
              // refresh na een intent-actie mag de form-state niet resetten).
              if (foundItem.soort) setSoort(foundItem.soort);
              if (foundItem.merk) setMerk(foundItem.merk);
              // Pre-fill dimension values - default to Rechthoek if no vorm set
              setVorm((foundItem.vorm as VormType) || 'Rechthoek');
              if (foundItem.lengthCm) setLengthCm(String(foundItem.lengthCm));
              if (foundItem.widthCm) setWidthCm(String(foundItem.widthCm));
              if (foundItem.diameterCm) setDiameterCm(String(foundItem.diameterCm));
              if (foundItem.aantal) setAantal(String(foundItem.aantal));
              // Note: wasrapport is NOT pre-filled - we show existing content separately
            }
          } else {
            setError('Item niet gevonden');
          }
        } else {
          throw new Error(response.message || 'Kon items niet ophalen');
        }
      } catch (err) {
        console.warn('API failed, trying cache:', err);

        // Fallback to cache
        try {
          const cached = await getCachedVoorinspectieItems();

          if (cached.length > 0) {
            // Map cached items to VoorinspectieItem type
            const mappedItems: VoorinspectieItem[] = cached.map((c) => ({
              itemId: c.itemId,
              itemName: c.itemName,
              productName: c.productName,
              werkOrderId: c.werkOrderId,
              werkOrderName: c.werkOrderName,
              companyCamProjectId: c.companyCamProjectId,
              createdDate: c.createdDate instanceof Date ? c.createdDate.toISOString() : c.createdDate,
              lengthCm: c.lengthCm,
              widthCm: c.widthCm,
              diameterCm: c.diameterCm,
              surfaceM2: c.surfaceM2,
              vorm: c.vorm,
              aantal: c.aantal,
              soort: c.soort,
              merk: c.merk,
              wasrapport: c.wasrapport,
              secondaryItems: c.secondaryItems || [],
              // Risico-flow defaults — cache bevat geen risico-data (online-only in MVP)
              risicoStatus: 'Geen',
              risicoTypes: null,
              risicoToelichting: null,
              risicoGemeldOp: null,
              risicoBeslistOp: null,
              risicoKlantReactie: null,
              klantverzoekReparatie: c.klantverzoekReparatie ?? null,
              klantverzoekStatus: c.klantverzoekStatus ?? null,
              klantverzoekBron: c.klantverzoekBron ?? null,
              klantverzoekNotitie: c.klantverzoekNotitie ?? null,
            }));

            setAllItems(mappedItems);
            const foundItem = mappedItems.find((i) => i.itemId === itemId);

            if (foundItem) {
              setItem(foundItem);
              if (!opts?.silent) {
                if (foundItem.soort) setSoort(foundItem.soort);
                if (foundItem.merk) setMerk(foundItem.merk);
                // Pre-fill dimension values from cache - default to Rechthoek if no vorm set
                setVorm((foundItem.vorm as VormType) || 'Rechthoek');
                if (foundItem.lengthCm) setLengthCm(String(foundItem.lengthCm));
                if (foundItem.widthCm) setWidthCm(String(foundItem.widthCm));
                if (foundItem.diameterCm) setDiameterCm(String(foundItem.diameterCm));
                if (foundItem.aantal) setAantal(String(foundItem.aantal));
                // Note: wasrapport is NOT pre-filled - we show existing content separately
              }
            } else {
              setError('Item niet gevonden in cache');
            }
          } else {
            setError('Geen verbinding en geen gecachte data');
          }
        } catch (cacheErr) {
          console.error('Cache also failed:', cacheErr);
          setError('Kon item niet laden');
        }
      } finally {
        if (!opts?.silent) setIsLoading(false);
      }
  }, [itemId]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  // Toggle voorinspectie option
  const toggleVoorinspectie = useCallback((optionValue: string) => {
    setSelectedVoorinspectie((prev) =>
      prev.includes(optionValue) ? prev.filter((o) => o !== optionValue) : [...prev, optionValue]
    );
  }, []);

  // Handle vorm change - clear irrelevant dimension fields
  const handleVormChange = useCallback((newVorm: VormType) => {
    setVorm(newVorm);

    // Clear dimension fields that don't apply to the new vorm
    if (newVorm === 'Rechthoek') {
      // Clear diameter and aantal when switching to Rechthoek
      setDiameterCm('');
      setAantal('');
    } else if (newVorm === 'Rond') {
      // Clear length, width and aantal when switching to Rond
      setLengthCm('');
      setWidthCm('');
      setAantal('');
    } else if (newVorm === 'Anders') {
      // Clear length, width and diameter when switching to Anders
      setLengthCm('');
      setWidthCm('');
      setDiameterCm('');
    }
  }, []);

  // Calculate area based on form input (real-time calculation)
  const calculatedOppervlakte = useMemo(() => {
    if (vorm === 'Rechthoek' && lengthCm && widthCm) {
      const l = parseFloat(lengthCm);
      const w = parseFloat(widthCm);
      if (!isNaN(l) && !isNaN(w) && l > 0 && w > 0) {
        return (l * w) / 10000;
      }
    }
    if (vorm === 'Rond' && diameterCm) {
      const d = parseFloat(diameterCm);
      if (!isNaN(d) && d > 0) {
        return (Math.PI * Math.pow(d / 2, 2)) / 10000;
      }
    }
    return null;
  }, [vorm, lengthCm, widthCm, diameterCm]);

  // Legacy oppervlakte from existing item data
  const existingOppervlakte = useMemo(() => {
    if (!item) return null;
    if (item.surfaceM2) return item.surfaceM2;
    if (item.lengthCm && item.widthCm) {
      return (item.lengthCm * item.widthCm) / 10000;
    }
    return null;
  }, [item]);

  // Find next item in queue
  const getNextItem = useCallback((): VoorinspectieItem | null => {
    if (!itemId || allItems.length <= 1) return null;
    const currentIndex = allItems.findIndex((i) => i.itemId === itemId);
    if (currentIndex === -1) return null;

    // Get next item (wrap around to first if at end)
    const nextIndex = (currentIndex + 1) % allItems.length;
    return allItems[nextIndex] ?? null;
  }, [itemId, allItems]);

  // Save inspection
  const handleSave = async (
    goToNext: boolean = false,
    opts?: { negeerRisicoIndicatie?: boolean }
  ) => {
    if (!item || !itemId) return;

    // Afrond-guard: schade-opties in de checklist zijn géén risico-melding —
    // dat zijn twee losse paden, en alleen aanvinken liet het kleed geruisloos
    // doorlopen naar reinigen zonder klantakkoord (WI-332384, 10-08). Loopt er
    // nog geen melding, dan eerst expliciet kiezen: melden of bewust zonder.
    if (!opts?.negeerRisicoIndicatie) {
      const heeftRisicoIndicatie = selectedVoorinspectie.some((v) =>
        RISICO_INDICATIE_VOORINSPECTIE.has(v)
      );
      const geenMelding = !item.risicoStatus || item.risicoStatus === 'Geen';
      if (heeftRisicoIndicatie && geenMelding) {
        setRisicoAfrondPrompt({ goToNext });
        return;
      }
    }

    setIsSaving(true);
    setSaveFout(null);

    // Build dimension data based on selected shape
    const dimensionData: Record<string, unknown> = {};
    if (vorm) {
      dimensionData.vorm = vorm;
      if (vorm === 'Rechthoek') {
        if (lengthCm) dimensionData.lengthCm = parseFloat(lengthCm);
        if (widthCm) dimensionData.widthCm = parseFloat(widthCm);
        dimensionData.isRoundRug = false;
        // Clear round/anders fields in Salesforce
        dimensionData.diameterCm = null;
        dimensionData.aantal = null;
      } else if (vorm === 'Rond') {
        if (diameterCm) dimensionData.diameterCm = parseFloat(diameterCm);
        dimensionData.isRoundRug = true;
        // Clear rechthoek/anders fields in Salesforce
        dimensionData.lengthCm = null;
        dimensionData.widthCm = null;
        dimensionData.aantal = null;
      } else if (vorm === 'Anders') {
        // For irregular shapes, aantal represents the estimated surface area in m²
        if (aantal) dimensionData.aantal = parseFloat(aantal);
        // Clear rechthoek/rond fields in Salesforce
        dimensionData.lengthCm = null;
        dimensionData.widthCm = null;
        dimensionData.diameterCm = null;
        dimensionData.isRoundRug = false;
      }
    }

    // Convert array to semicolon-separated string for Salesforce multi-picklist
    const data = {
      ...dimensionData,
      voorinspectie: selectedVoorinspectie.length > 0 ? selectedVoorinspectie.join(';') : undefined,
      soort: soort || undefined,
      merk: merk || undefined,
      voorinspectieOpmerkingen: voorinspectieOpmerkingen || undefined,
      wasrapport: wasrapport || undefined,
      markComplete: true,
    };

    console.log('💾 Saving voorinspectie:', { itemId, data });

    try {
      // Try API first
      const response = await salesforceClient.saveVoorinspectie(itemId, data);
      console.log('✅ Save response:', response);

      if (response.success) {
        // Voorinspectie is klaar, ook als er een risico op het kleed loopt. Het
        // kleed verdwijnt dus altijd uit de lijst; wat blijft wachten is de
        // statuswissel naar 'Klaar voor Reinigen', en die zet kantoor zodra het
        // risico is afgehandeld. Het oude defer-pad ("kom later nog eens
        // afronden") bestaat niet meer.
        await removeFromVoorinspectieCache(itemId);

        // Haptic feedback on success
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        if (goToNext) {
          const nextItem = getNextItem();
          if (nextItem) {
            // Remove current item from allItems state so next calculation updates
            setAllItems((prev) => prev.filter((i) => i.itemId !== itemId));
            navigate(`/voorinspectie/${nextItem.itemId}`, { replace: true });
          } else {
            navigate('/voorinspectie');
          }
        } else {
          navigate('/voorinspectie');
        }
      } else {
        throw new Error(response.message || 'Opslaan mislukt');
      }
    } catch (err) {
      if (isServerWeigering(err)) {
        setSaveFout(err.message);
        if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 100]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      console.warn('API save failed, queuing offline:', err);

      // Queue for offline sync with dimension data (lib/sync/wasserij-wachtrij.ts verstuurt)
      await queuePendingVoorinspectie({
        itemId,
        itemName: item?.itemName,
        lengthCm: dimensionData.lengthCm as number | undefined,
        widthCm: dimensionData.widthCm as number | undefined,
        diameterCm: dimensionData.diameterCm as number | undefined,
        isRoundRug: dimensionData.isRoundRug as boolean | undefined,
        vorm: dimensionData.vorm as string | undefined,
        aantal: dimensionData.aantal as number | undefined,
        voorinspectie: data.voorinspectie as string | undefined,
        soort: data.soort as string | undefined,
        merk: data.merk as string | undefined,
        voorinspectieOpmerkingen: data.voorinspectieOpmerkingen as string | undefined,
        wasrapport: data.wasrapport as string | undefined,
        markComplete: true,
      });

      // Remove from cache (it's "done" locally)
      await removeFromVoorinspectieCache(itemId);

      // Still navigate (optimistic UI)
      if (navigator.vibrate) {
        navigator.vibrate([50, 30, 50]); // Different pattern for offline
      }

      if (goToNext) {
        const nextItem = getNextItem();
        if (nextItem) {
          setAllItems((prev) => prev.filter((i) => i.itemId !== itemId));
          navigate(`/voorinspectie/${nextItem.itemId}`, { replace: true });
        } else {
          navigate('/voorinspectie');
        }
      } else {
        navigate('/voorinspectie');
      }
    } finally {
      setIsSaving(false);
    }
  };

  /** Na een risico-melding: badge direct op Gemeld, zonder herladen. */
  const markeerGemeld = useCallback(() => {
    setItem((prev) =>
      prev
        ? {
            ...prev,
            risicoStatus: 'Gemeld',
            risicoTypes: prev.risicoTypes,
            risicoGemeldOp: new Date().toISOString(),
          }
        : prev
    );
    setRisicoDialogOpen(false);
  }, []);

  const nextItem = getNextItem();
  const positie = allItems.findIndex((i) => i.itemId === itemId) + 1;

  return {
    itemId,
    item, allItems, isLoading, error, loadItem, saveFout,
    vorm, lengthCm, setLengthCm, widthCm, setWidthCm, diameterCm, setDiameterCm, aantal, setAantal,
    handleVormChange, calculatedOppervlakte, existingOppervlakte,
    selectedVoorinspectie, toggleVoorinspectie,
    soort, setSoort, merk, setMerk,
    voorinspectieOpmerkingen, setVoorinspectieOpmerkingen, wasrapport, setWasrapport,
    isSaving, handleSave,
    risicoDialogOpen, setRisicoDialogOpen, risicoAfrondPrompt, setRisicoAfrondPrompt, markeerGemeld,
    showCamera, setShowCamera, mediaRefresh, setMediaRefresh,
    nextItem, positie,
  };
}

export type VoorinspectieItemState = ReturnType<typeof useVoorinspectieItem>;
